import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  orderBy,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { getCompanyInfo, getDefaultCompanyInfo } from '../../../services/companyInfoService';

export const useDocumentOperations = ({ 
  db, user, documents, setDocuments, setSelectedDocument, 
  setDocumentContent, setIsLoading, genericDocumentTypes 
}) => {
  
  // Générer un template avec les informations de la société - Style livret.html
  const generateTemplateWithCompanyInfo = (template, companyInfo) => {
    const currentDate = new Date().toLocaleDateString('fr-FR');
    
    // Style CSS basé sur livret.html avec tous les éléments
    const cssStyle = `
      :root{
        --brand:#125E8A;         /* bleu principal */
        --brand-alt:#0B3E59;     /* bleu foncé */
        --accent:#27A2DB;        /* bleu clair */
        --ink:#1f2937;           /* texte */
        --muted:#6b7280;         /* texte secondaire */
        --line:#e5e7eb;          /* bordures */
        --bg:#ffffff;            /* fond */
        --chip:#f3f4f6;          /* pastilles */
      }

      /* Print button styles */
      .print-btn {
        display: inline-block;
        background: var(--brand);
        color: white;
        padding: 10px 20px;
        border-radius: 6px;
        text-decoration: none;
        font-weight: 600;
        margin: 20px 0;
        cursor: pointer;
        border: none;
        font-family: inherit;
      }

      .print-btn:hover {
        background: var(--brand-alt);
      }

      .print-btn:active {
        transform: translateY(1px);
      }

      /* Reset léger + typographie */
      *{box-sizing:border-box}
      html,body{margin:0;padding:0;background:var(--bg);color:var(--ink);font-family:Inter,system-ui,Segoe UI,Roboto,Helvetica,Arial,sans-serif; line-height:1.55}
      h1,h2,h3{line-height:1.25;margin:0 0 .6rem}
      h1{font-size:clamp(1.8rem,2.5vw,2.4rem);font-weight:800;color:var(--brand)}
      h2{font-size:clamp(1.3rem,2vw,1.6rem);font-weight:700;color:var(--brand-alt);margin-top:2.2rem}
      h3{font-size:1.05rem;font-weight:700;color:var(--ink);margin-top:1.2rem}
      p{margin:.35rem 0 .75rem}
      a{color:var(--brand);text-decoration:none}
      a:hover{text-decoration:underline}
      ul,ol{margin:.5rem 0 .9rem 1.1rem}
      .wrap{max-width:980px;margin:0 auto;padding:28px 20px 64px}

      /* En-tête */
      header{
        display:flex;gap:24px;align-items:center;padding:24px 0 20px;border-bottom:2px solid var(--brand);margin-bottom:24px
      }
      .logo{
        width:140px;height:auto;flex:0 0 auto;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,.08);background:#fff;padding:8px
      }
      .head-title{display:flex;flex-direction:column;flex:1}
      .brand{font-size:1.8rem;font-weight:800;letter-spacing:.3px;color:var(--brand);margin-bottom:4px}
      .subtitle{color:var(--muted);font-size:1.05rem;font-weight:500}

      /* Sommaire */
      .toc{
        display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:10px;margin:14px 0 24px
      }
      .toc a{
        display:block;padding:10px 12px;border:1px solid var(--line);border-radius:10px;background:#fff
      }

      /* Cartes de section */
      section{padding:12px 0}
      .card{
        border:1px solid var(--line);border-radius:14px;padding:18px 16px;background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.03)
      }
      .meta{display:flex;flex-wrap:wrap;gap:10px;margin:.5rem 0 0}
      .chip{background:var(--chip);color:var(--muted);padding:6px 10px;border-radius:999px;font-size:.9rem}

      /* Tableaux */
      .table{
        width:100%;border-collapse:separate;border-spacing:0;border:1px solid var(--line);border-radius:12px;overflow:hidden;margin:.6rem 0 1rem
      }
      .table th,.table td{padding:10px 12px;border-bottom:1px solid var(--line);vertical-align:top}
      .table thead th{background:#f9fafb;font-weight:700;text-align:left}
      .table tr:last-child td{border-bottom:none}
      .note{font-size:.92rem;color:var(--muted)}

      /* Encarts */
      .callout{
        border-left:4px solid var(--accent);background:#f0f9ff;padding:12px 14px;border-radius:8px;margin:.7rem 0 1rem
      }

      /* Footer */
      footer{margin-top:36px;padding-top:18px;border-top:1px solid var(--line);color:var(--muted);font-size:.92rem}

      /* Impression */
      @media print{
        header{border:none;margin:0 0 6px}
        .wrap{padding:0}
        a{color:inherit;text-decoration:none}
        .card{box-shadow:none;page-break-inside:avoid}
        .table{page-break-inside:auto}
        .toc{display:block}
        .toc a{border:none;padding:0}
      }
    `;

    // Header avec logo et informations de la société
    const headerHTML = `
      <header>
        ${companyInfo.logoUrl ? `<img class="logo" alt="Logo ${companyInfo.denominationSociale}" src="${companyInfo.logoUrl}"/>` : ''}
        <div class="head-title">
          <h1>${template.label} – ${companyInfo.denominationSociale}</h1>
          <div class="subtitle">Document officiel — version du ${currentDate}</div>
        </div>
      </header>
    `;

    // Footer avec informations de contact
    const footerHTML = `
      <footer>
        © ${companyInfo.denominationSociale} — ${companyInfo.siegeSocial} — 
        ${companyInfo.telephone ? `Tél : ${companyInfo.telephone}` : ''} — 
        ${companyInfo.email ? `Email : ${companyInfo.email}` : ''}
      </footer>
    `;

    // Contenu spécifique selon le type de document
    let contentHTML = '';
    
    switch(template.id) {
      case 'LIVRET_ACCUEIL':
        contentHTML = `
          <!-- 1. Édito / mot de bienvenue -->
    <section id="edito">
      <div class="card">
        <h2>Bienvenue</h2>
        <p>Nous sommes heureux de vous accueillir parmi nos bénéficiaires. Ce livret a pour objectif de vous présenter notre structure, nos services et les modalités de notre collaboration.</p>
      </div>
    </section>

    <!-- 2. Présentation de la structure -->
    <section id="presentation">
      <div class="card">
        <h2>Notre Structure</h2>
        <p>Notre organisation est spécialisée dans l'accompagnement à domicile et s'engage à vous offrir des services de qualité adaptés à vos besoins.</p>
      </div>
    </section>

    <!-- 3. Nos valeurs -->
    <section id="valeurs">
      <div class="card">
        <h2>Nos Valeurs</h2>
        <ul>
          <li><strong>Respect</strong> : De la personne, de son intimité et de ses choix</li>
          <li><strong>Écoute</strong> : Adaptation à vos besoins spécifiques</li>
          <li><strong>Professionnalisme</strong> : Compétence et sérieux dans nos interventions</li>
          <li><strong>Bienveillance</strong> : Accompagnement personnalisé et attentionné</li>
        </ul>
      </div>
    </section>

    <!-- 4. Nos services -->
    <section id="services">
      <div class="card">
        <h2>Nos Services</h2>
        <ul>
          <li>Aide à la toilette et à l'habillage</li>
          <li>Aide aux repas</li>
          <li>Accompagnement dans les déplacements</li>
          <li>Aide aux courses et à la préparation des repas</li>
          <li>Entretien du logement</li>
          <li>Soutien administratif</li>
        </ul>
      </div>
    </section>

    <!-- 5. Vos interlocuteurs -->
    <section id="interlocuteurs">
      <div class="card">
        <h2>Vos Interlocuteurs</h2>
        <p><strong>Responsable de secteur :</strong> Votre interlocuteur privilégié pour toute question concernant votre prise en charge.</p>
        <p><strong>Intervenants :</strong> Des professionnels formés et qualifiés qui vous accompagnent au quotidien.</p>
      </div>
    </section>

    <!-- 6. Horaires et disponibilités -->
    <section id="horaires">
      <div class="card">
        <h2>Horaires et Disponibilités</h2>
        <p>Nos services sont disponibles du lundi au vendredi de 7h à 20h, et le samedi de 8h à 18h.</p>
        <p>Pour les urgences en dehors de ces horaires, un numéro de permanence est à votre disposition.</p>
      </div>
    </section>

    <!-- 7. Sécurité et confidentialité -->
    <section id="securite">
      <div class="card">
        <h2>Sécurité et Confidentialité</h2>
        <p>Tous nos intervenants sont soumis au secret professionnel. Vos informations personnelles sont traitées avec la plus grande confidentialité.</p>
      </div>
    </section>`;
        break;
        
      case 'CHARTE_DROITS':
        contentHTML = `
          <div class="card">
            <h2>Préambule</h2>
            <p>${companyInfo.denominationSociale} s'engage à respecter et à faire respecter les droits fondamentaux de chaque personne accompagnée. Cette charte définit les principes essentiels qui guident notre action au quotidien.</p>
          </div>

          <div class="card">
            <h2>Droits fondamentaux</h2>
            <div class="callout">
              <strong>Article 1 - Droit à la dignité</strong>
              <p>Toute personne a droit au respect de sa dignité, de son intégrité, de sa vie privée, de son intimité et de sa sécurité.</p>
            </div>
            <div class="callout">
              <strong>Article 2 - Droit à l'autonomie</strong>
              <p>Chaque personne a le droit de conserver son autonomie, de faire ses propres choix et de participer aux décisions qui la concernent.</p>
            </div>
            <div class="callout">
              <strong>Article 3 - Droit à l'information</strong>
              <p>Toute personne a le droit d'être informée sur son état de santé, les soins proposés, les intervenants et les modalités de prise en charge.</p>
            </div>
          </div>
        `;
        break;
        
      case 'REGLEMENT_INTERIEUR':
        contentHTML = `
          <div class="card">
            <h2>Préambule</h2>
            <p>Le présent règlement intérieur a pour objet de définir les règles de fonctionnement de ${companyInfo.denominationSociale} afin d'assurer le bon déroulement des interventions et le respect mutuel entre tous les acteurs.</p>
          </div>

          <div class="card">
            <h2>Article 1 - Objet</h2>
            <p>Ce règlement s'applique à l'ensemble des bénéficiaires, intervenants et visiteurs de ${companyInfo.denominationSociale}.</p>
          </div>

          <div class="card">
            <h2>Article 2 - Horaires d'intervention</h2>
            <p>Les interventions ont lieu selon les plages horaires convenues dans le contrat de prestation :</p>
            <ul>
              <li>Du lundi au vendredi : 7h00 - 20h00</li>
              <li>Samedi : 8h00 - 18h00</li>
              <li>Dimanche et jours fériés : Interventions exceptionnelles sur demande</li>
            </ul>
          </div>
        `;
        break;
        
      case 'CGS':
        contentHTML = `
          <div class="card">
            <h2>Article 1 - Objet</h2>
            <p>Les présentes conditions générales ont pour objet de définir les modalités de fourniture des services par ${companyInfo.denominationSociale} à ses clients.</p>
          </div>

          <div class="card">
            <h2>Article 2 - Définitions</h2>
            <div class="callout">
              <strong>Client</strong>
              <p>Personne physique ou morale bénéficiant des services de ${companyInfo.denominationSociale}.</p>
            </div>
            <div class="callout">
              <strong>Service</strong>
              <p>Prestation d'aide à domicile fournie par ${companyInfo.denominationSociale}.</p>
            </div>
          </div>

          <div class="card">
            <h2>Article 3 - Prestations</h2>
            <p>${companyInfo.denominationSociale} s'engage à fournir les prestations décrites dans le contrat de service, avec professionnalisme et dans le respect des règles déontologiques.</p>
          </div>
        `;
        break;
        
      default:
        contentHTML = template.template;
    }

    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${template.label} – ${companyInfo.denominationSociale}</title>
  <meta name="description" content="${template.description}" />
  <style>${cssStyle}</style>
</head>
<body>
  <div class="wrap">
    ${headerHTML}
    ${contentHTML}
    ${footerHTML}
  </div>
</body>
</html>`;
  };

  // Charger les documents génériques existants et créer ceux qui manquent
  const loadGenericDocuments = async (forceUpdateSelected = false) => {
    try {
      setIsLoading(true);
      console.log('Chargement des documents génériques...');
      
      const q = query(
        collection(db, 'genericDocuments'),
        orderBy('metadata.updatedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const existingDocs = [];
      querySnapshot.forEach((doc) => {
        existingDocs.push({ id: doc.id, ...doc.data() });
      });
      
      console.log('Documents chargés:', existingDocs.length);
      
      // Vérifier quels templates existent déjà
      const existingTypes = existingDocs.map(doc => doc.documentType);
      
      // Créer les documents manquants
      const missingTemplates = genericDocumentTypes.filter(
        template => !existingTypes.includes(template.id)
      );
      
      if (missingTemplates.length > 0) {
        console.log('Création des documents manquants:', missingTemplates.length);
        await createMissingDocuments(missingTemplates, existingDocs);
      } else {
        setDocuments(existingDocs);
        console.log('Documents mis à jour dans l\'état:', existingDocs);
        
        // Mettre à jour le document sélectionné s'il existe et si demandé
        if (forceUpdateSelected && setSelectedDocument) {
          // Récupérer le document sélectionné actuel depuis l'état local
          const currentSelectedDoc = documents.find(doc => doc.id === documents[0]?.id);
          if (currentSelectedDoc) {
            const updatedDoc = existingDocs.find(doc => doc.id === currentSelectedDoc.id);
            if (updatedDoc) {
              console.log('Rechargement du document sélectionné:', updatedDoc.documentName);
              setSelectedDocument(updatedDoc);
              setDocumentContent(updatedDoc.content);
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des documents génériques:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Créer les documents manquants
  const createMissingDocuments = async (missingTemplates, existingDocs) => {
    try {
      const newDocs = [...existingDocs];
      
      // Récupérer les informations de la société
      let companyInfo;
      try {
        companyInfo = await getCompanyInfo(db);
      } catch (error) {
        console.warn('Impossible de récupérer les infos société, utilisation des valeurs par défaut');
        companyInfo = getDefaultCompanyInfo();
      }
      
      for (const template of missingTemplates) {
        // Utiliser le template générique directement (sans génération avec infos société)
        const templateContent = template.template;
        
        const documentData = {
          documentType: template.id,
          documentName: template.label,
          description: template.description,
          content: templateContent,
          version: '1.0',
          status: 'active',
          metadata: {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            updatedBy: user?.uid || 'system'
          }
        };
        
        const docRef = doc(collection(db, 'genericDocuments'));
        await setDoc(docRef, documentData);
        newDocs.push({ id: docRef.id, ...documentData });
      }
      
      setDocuments(newDocs);
      
    } catch (error) {
      console.error('Erreur lors de la création des documents manquants:', error);
    }
  };

  // Réinitialiser un document à son template d'origine
  const resetDocument = async (document) => {
    try {
      setIsLoading(true);
      const template = genericDocumentTypes.find(t => t.id === document.documentType);
      
      if (!template) {
        alert('Template non trouvé');
        return;
      }

      // Utiliser le template générique directement
      await updateDoc(doc(db, 'genericDocuments', document.id), {
        content: template.template,
        version: '1.0',
        'metadata.updatedAt': serverTimestamp(),
        'metadata.updatedBy': user?.uid || 'system'
      });
      
      await loadGenericDocuments();
      alert('Document réinitialisé au template d\'origine');
      
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      alert('Erreur lors de la réinitialisation');
    } finally {
      setIsLoading(false);
    }
  };

  // Convertir le contenu édité en HTML complet avec header/footer (version asynchrone pour sauvegarde)
  const convertToFullHTML = async (content, selectedDocument) => {
    const currentDate = new Date().toLocaleDateString('fr-FR');
    
    // Récupérer les informations de la société depuis Firestore
    let companyInfo;
    try {
      companyInfo = await getCompanyInfo(db);
    } catch (error) {
      console.warn('Impossible de récupérer les infos société, utilisation des valeurs par défaut');
      companyInfo = getDefaultCompanyInfo();
    }
    
    // Style CSS basé sur livret.html
    const cssStyle = `
      :root{
        --brand:#125E8A;         /* bleu principal */
        --brand-alt:#0B3E59;     /* bleu foncé */
        --accent:#27A2DB;        /* bleu clair */
        --ink:#1f2937;           /* texte */
        --muted:#6b7280;         /* texte secondaire */
        --line:#e5e7eb;          /* bordures */
        --bg:#ffffff;            /* fond */
        --chip:#f3f4f6;          /* pastilles */
      }

      /* Print button styles */
      .print-btn {
        display: inline-block;
        background: var(--brand);
        color: white;
        padding: 10px 20px;
        border-radius: 6px;
        text-decoration: none;
        font-weight: 600;
        margin: 20px 0;
        cursor: pointer;
        border: none;
        font-family: inherit;
      }

      .print-btn:hover {
        background: var(--brand-alt);
      }

      .print-btn:active {
        transform: translateY(1px);
      }

      /* Reset léger + typographie */
      *{box-sizing:border-box}
      html,body{margin:0;padding:0;background:var(--bg);color:var(--ink);font-family:Inter,system-ui,Segoe UI,Roboto,Helvetica,Arial,sans-serif; line-height:1.55}
      h1,h2,h3{line-height:1.25;margin:0 0 .6rem}
      h1{font-size:clamp(1.8rem,2.5vw,2.4rem);font-weight:800;color:var(--brand)}
      h2{font-size:clamp(1.3rem,2vw,1.6rem);font-weight:700;color:var(--brand-alt);margin-top:2.2rem}
      h3{font-size:1.05rem;font-weight:700;color:var(--ink);margin-top:1.2rem}
      p{margin:.35rem 0 .75rem}
      a{color:var(--brand);text-decoration:none}
      a:hover{text-decoration:underline}
      ul,ol{margin:.5rem 0 .9rem 1.1rem}
      .wrap{max-width:980px;margin:0 auto;padding:28px 20px 64px}

      /* En-tête */
      header{
        display:flex;gap:24px;align-items:center;padding:24px 0 20px;border-bottom:2px solid var(--brand);margin-bottom:24px
      }
      .logo{
        width:140px;height:auto;flex:0 0 auto;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,.08);background:#fff;padding:8px
      }
      .head-title{display:flex;flex-direction:column;flex:1}
      .brand{font-size:1.8rem;font-weight:800;letter-spacing:.3px;color:var(--brand);margin-bottom:4px}
      .subtitle{color:var(--muted);font-size:1.05rem;font-weight:500}

      /* Sommaire */
      .toc{
        display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:10px;margin:14px 0 24px
      }
      .toc a{
        display:block;padding:10px 12px;border:1px solid var(--line);border-radius:10px;background:#fff
      }

      /* Cartes de section */
      section{padding:12px 0}
      .card{
        border:1px solid var(--line);border-radius:14px;padding:18px 16px;background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.03)
      }
      .meta{display:flex;flex-wrap:wrap;gap:10px;margin:.5rem 0 0}
      .chip{background:var(--chip);color:var(--muted);padding:6px 10px;border-radius:999px;font-size:.9rem}

      /* Tableaux */
      .table{
        width:100%;border-collapse:separate;border-spacing:0;border:1px solid var(--line);border-radius:12px;overflow:hidden;margin:.6rem 0 1rem
      }
      .table th,.table td{padding:10px 12px;border-bottom:1px solid var(--line);vertical-align:top}
      .table thead th{background:#f9fafb;font-weight:700;text-align:left}
      .table tr:last-child td{border-bottom:none}
      .note{font-size:.92rem;color:var(--muted)}

      /* Encarts */
      .callout{
        border-left:4px solid var(--accent);background:#f0f9ff;padding:12px 14px;border-radius:8px;margin:.7rem 0 1rem
      }

      /* Footer */
      footer{margin-top:36px;padding-top:18px;border-top:1px solid var(--line);color:var(--muted);font-size:.92rem}

      /* Impression */
      @media print{
        header{border:none;margin:0 0 6px}
        .wrap{padding:0}
        a{color:inherit;text-decoration:none}
        .card{box-shadow:none;page-break-inside:avoid}
        .table{page-break-inside:auto}
        .toc{display:block}
        .toc a{border:none;padding:0}
      }
    `;

    // Header avec logo et informations de la société
    const headerHTML = `
      <header>
        ${companyInfo.logoUrl ? `<img class="logo" alt="Logo ${companyInfo.denominationSociale}" src="${companyInfo.logoUrl}"/>` : ''}
        <div class="head-title">
          <h1>${selectedDocument?.documentName || 'Document'} – ${companyInfo.denominationSociale}</h1>
          <div class="subtitle">Document officiel — version du ${currentDate}</div>
        </div>
      </header>
    `;

    // Footer avec informations de contact
    const footerHTML = `
      <footer>
        © ${companyInfo.denominationSociale} — ${companyInfo.siegeSocial} — 
        ${companyInfo.telephone ? `Tél : ${companyInfo.telephone}` : ''} — 
        ${companyInfo.email ? `Email : ${companyInfo.email}` : ''}
      </footer>
    `;

    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${selectedDocument?.documentName || 'Document'} – ${companyInfo.denominationSociale}</title>
  <meta name="description" content="${selectedDocument?.description || 'Document générique'}" />
  <style>${cssStyle}</style>
</head>
<body>
  <div class="wrap">
    ${headerHTML}
    ${content}
    ${footerHTML}
  </div>
</body>
</html>`;
  };

  // Sauvegarder les modifications
  const saveEdit = async (selectedDocument, editContent) => {
    try {
      setIsLoading(true);
      console.log('Début sauvegarde - Document ID:', selectedDocument.id);
      console.log('Contenu à sauvegarder:', editContent.substring(0, 100) + '...');
      
      // Convertir le contenu en HTML complet
      const fullHTML = await convertToFullHTML(editContent, selectedDocument);
      
      // Calculer la nouvelle version
      const currentVersion = parseFloat(selectedDocument.version) || 1.0;
      const newVersion = (currentVersion + 0.1).toFixed(1);
      
      // Préparer les données de mise à jour
      const updateData = {
        content: fullHTML,
        version: newVersion,
        'metadata.updatedAt': serverTimestamp(),
        'metadata.updatedBy': user?.uid || 'system'
      };
      
      console.log('Données de mise à jour:', updateData);
      
      // Sauvegarder dans Firestore
      await updateDoc(doc(db, 'genericDocuments', selectedDocument.id), updateData);
      
      console.log('Sauvegarde réussie');
      
      // Fermer le modal et recharger les documents
      await loadGenericDocuments();
      alert('Document modifié avec succès - Version ' + newVersion);
      
      // Retourner true pour indiquer que la sauvegarde a réussi
      return true;
      
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      console.error('Détails de l\'erreur:', error.message);
      alert('Erreur lors de la modification: ' + error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Supprimer un document
  const deleteDocument = async (documentToDelete) => {
    try {
      setIsLoading(true);
      await deleteDoc(doc(db, 'genericDocuments', documentToDelete.id));
      
      setSelectedDocument(null);
      await loadGenericDocuments();
      alert('Document supprimé avec succès');
      
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setIsLoading(false);
    }
  };

  // Créer un nouveau document personnalisé
  const createDocument = async (documentData) => {
    try {
      setIsLoading(true);
      console.log('Création d\'un nouveau document:', documentData);

      // Récupérer les informations de la société
      let companyInfo;
      try {
        companyInfo = await getCompanyInfo(db);
      } catch (error) {
        console.warn('Impossible de récupérer les infos société, utilisation des valeurs par défaut');
        companyInfo = getDefaultCompanyInfo();
      }

      // Convertir le contenu en HTML complet avec header/footer
      const fullHTML = await convertToFullHTML(documentData.initialContent, {
        documentName: documentData.documentName,
        description: documentData.description
      });

      // Générer un ID unique pour le type de document personnalisé
      const customDocumentType = `CUSTOM_${Date.now()}`;

      const documentToCreate = {
        documentType: customDocumentType,
        documentName: documentData.documentName,
        description: documentData.description,
        content: fullHTML,
        version: '1.0',
        status: 'active',
        category: documentData.documentType,
        isCustom: true,
        metadata: {
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || 'system',
          createdBy: user?.uid || 'system'
        }
      };

      const docRef = doc(collection(db, 'genericDocuments'));
      await setDoc(docRef, documentToCreate);
      
      console.log('Document créé avec succès:', docRef.id);
      
      // Recharger les documents pour afficher le nouveau
      await loadGenericDocuments();
      alert('Document créé avec succès');
      
      return true;
      
    } catch (error) {
      console.error('Erreur lors de la création du document:', error);
      alert('Erreur lors de la création du document: ' + error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    loadGenericDocuments,
    resetDocument,
    saveEdit,
    deleteDocument,
    createDocument
  };
};
